import React, { useEffect, useState, useRef } from "react";
import { useStore } from "./store";
import { useAutoRefresh } from "./hooks/useAutoRefresh";
import { EnergyBattery } from "./components";
import { LiveStakingTicker } from "./screens4";
import { COUNTRY_OPTIONS } from "./site/countries";
import { Eye, EyeOff } from "lucide-react";
import {
  Home as HomeIcon, TrendingUp, CreditCard, Users, ShoppingBag, BarChart3,
  Bot, ArrowLeftRight, Copy, Check, ArrowUpRight, ArrowDownRight, Zap,
  Gift, Lock, ChevronRight, X, Search, MapPin, Star, Send, Sparkles,
  QrCode, Wallet as WalletIcon, PiggyBank, Shield, ShoppingCart, Plus, Minus
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { createChart, CandlestickSeries, HistogramSeries } from "lightweight-charts";
import api from "./api";

/* ═════════════════════════════════════════════════════════════════
   AUTH SCREEN (Welcome / Login / Register)
═════════════════════════════════════════════════════════════════ */
export function AuthScreen() {
  const [mode, setMode] = useState("welcome"); // welcome | login | register
  return <AuthScreenInner mode={mode} setMode={setMode} key={mode} />;
}

function AuthScreenInner({ mode, setMode }) {
  const { login, register, setToast } = useStore();
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [emailErr, setEmailErr] = useState(null);
  const [form, setForm] = useState({
    firstName: "", lastName: "", email: "", password: "",
    phone: "", country: "", dateOfBirth: "", referralCode: "", telegram: ""
  });
  const ref = new URLSearchParams(window.location.search).get("ref");
  useEffect(() => { if (ref) setForm((f) => ({ ...f, referralCode: ref })); }, [ref]);

  // Auto-detect country via geo-IP (graceful null fallback if upstream fails).
  useEffect(() => {
    if (mode !== "register") return;
    api.get("/geo/lookup").then((r) => {
      const cc = (r.data?.country || "").toUpperCase();
      if (cc && cc.length === 2) setForm((f) => (f.country ? f : { ...f, country: cc }));
    }).catch(() => {});
  }, [mode]);

  // Live email-exists check (debounced 500ms) during registration.
  useEffect(() => {
    if (mode !== "register") { setEmailErr(null); return; }
    const e = form.email.trim().toLowerCase();
    if (!e || !e.includes("@")) { setEmailErr(null); return; }
    const handle = setTimeout(async () => {
      try {
        const r = await api.get(`/auth/email-exists?email=${encodeURIComponent(e)}`);
        setEmailErr(r.data?.exists ? "Этот Email уже зарегистрирован" : null);
      } catch (_e) { /* network glitch — ignore, keep last state */ }
    }, 500);
    return () => clearTimeout(handle);
  }, [form.email, mode]);

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "register" && emailErr) { setToast("error", emailErr); return; }
    if (mode === "register" && !form.country) { setToast("error", "Выберите страну проживания"); return; }
    if (mode === "register" && !form.referralCode.trim()) {
      setToast("error", "Реферальный код обязателен. Возьмите ссылку у пригласителя.");
      return;
    }
    setLoading(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else await register(form);
    } catch (err) {
      setToast("error", err.response?.data?.detail || "Ошибка");
    } finally { setLoading(false); }
  };

  if (mode === "welcome") {
    return (
      <div className="screen" style={{ padding: 0, background: "linear-gradient(135deg, #0D1B4B 0%, #1A3A8F 60%, #0E6B5E 100%)" }}>
        <div style={{ padding: "60px 24px 30px", color: "white", textAlign: "center" }}>
          <div style={{ fontSize: 60, marginBottom: 20 }}>🚀</div>
          <h1 style={{ fontSize: 32, fontWeight: 900, margin: 0, letterSpacing: -1 }}>TURBO GLOBAL</h1>
          <p style={{ fontSize: 14, opacity: 0.85, margin: "8px 0 40px" }}>
            Гибридная глобальная экономика<br/>для 1 миллиарда пользователей
          </p>
          <div style={{ background: "rgba(255,255,255,0.08)", borderRadius: 24, padding: 24, marginBottom: 24, textAlign: "left", backdropFilter: "blur(10px)" }}>
            {[
              { icon: "💰", text: "4 кошелька: Cash, Shopping, Pension 8%, Insurance" },
              { icon: "💳", text: "Virtual Visa + оплата в 100M+ магазинов" },
              { icon: "📈", text: "Стейкинг 12-32% APY, 6 пакетов" },
              { icon: "👥", text: "8-уровневая реферальная сеть" },
              { icon: "🎁", text: "Бонус TGC на €100 при регистрации" },
            ].map((f, i) => (
              <div key={i} style={{ display: "flex", gap: 14, marginBottom: 14, fontSize: 13 }}>
                <span style={{ fontSize: 22 }}>{f.icon}</span>
                <span style={{ flex: 1, paddingTop: 4, opacity: 0.9 }}>{f.text}</span>
              </div>
            ))}
          </div>
        </div>
        <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 10 }}>
          <button className="btn btn-gold btn-full" onClick={() => setMode("register")} data-testid="welcome-register-btn">
            Начать — получить TGC на €100 🎁
          </button>
          <button className="btn btn-outline btn-full" style={{ color: "white", borderColor: "rgba(255,255,255,0.3)" }} onClick={() => setMode("login")} data-testid="welcome-login-btn">
            У меня уже есть аккаунт
          </button>
          <p style={{ color: "rgba(255,255,255,0.5)", fontSize: 11, textAlign: "center", marginTop: 12 }}>
            DEMO версия • Все функции работают с тестовыми TGC
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="screen" style={{ background: "white" }}>
      <div style={{ padding: "24px 20px 20px" }}>
        <button onClick={() => setMode("welcome")} style={{ background: "transparent", border: "none", fontSize: 14, color: "var(--navy)", marginBottom: 20, cursor: "pointer" }}>
          ← Назад
        </button>
        <h1 style={{ fontSize: 26, fontWeight: 800, color: "var(--navy)", margin: "0 0 8px" }}>
          {mode === "login" ? "Вход" : "Регистрация"}
        </h1>
        <p className="text-muted text-sm mb-lg">
          {mode === "login" ? "С возвращением в Turbo Global" : "Создай аккаунт и получи TGC на €100"}
        </p>
        <form onSubmit={submit} className="col gap-md">
          {mode === "register" && (
            <>
              <div className="grid-2">
                <div>
                  <label className="input-label">Имя</label>
                  <input required className="input" value={form.firstName} onChange={(e) => setForm({...form, firstName: e.target.value})} data-testid="reg-firstname" />
                </div>
                <div>
                  <label className="input-label">Фамилия</label>
                  <input required className="input" value={form.lastName} onChange={(e) => setForm({...form, lastName: e.target.value})} data-testid="reg-lastname" />
                </div>
              </div>
              <div>
                <label className="input-label">Телефон</label>
                <input className="input" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} placeholder="+7 701 234 5678" data-testid="reg-phone" />
              </div>
              <div className="grid-2">
                <div>
                  <label className="input-label">Страна *</label>
                  <select required className="input" value={form.country} onChange={(e) => setForm({...form, country: e.target.value})} data-testid="reg-country">
                    <option value="">— Выберите страну —</option>
                    {COUNTRY_OPTIONS.map((c) => (
                      <option key={c.code} value={c.code}>{c.flag} {c.ru} ({c.code})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="input-label">Дата рождения</label>
                  <input type="date" className="input" value={form.dateOfBirth} onChange={(e) => setForm({...form, dateOfBirth: e.target.value})} data-testid="reg-dob" />
                </div>
              </div>
            </>
          )}
          <div>
            <label className="input-label">Email</label>
            <input required type="email" className="input" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} data-testid="auth-email" />
            {emailErr && (
              <div data-testid="reg-email-err" style={{ marginTop: 4, color: "#FF3D6C", fontSize: 12 }}>{emailErr}</div>
            )}
          </div>
          <div>
            <label className="input-label">Пароль</label>
            <div style={{ position: "relative" }}>
              <input required type={showPwd ? "text" : "password"} minLength="6" className="input" value={form.password}
                onChange={(e) => setForm({...form, password: e.target.value})} data-testid="auth-password"
                style={{ paddingRight: 44 }} />
              <button type="button" onClick={() => setShowPwd(!showPwd)} data-testid="auth-password-toggle"
                aria-label={showPwd ? "Скрыть пароль" : "Показать пароль"}
                style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)",
                  width: 34, height: 34, borderRadius: 8, border: "1px solid #d8d8e0",
                  background: "#f5f5fa", color: "#0D1B4B", cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center" }}>
                {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          {mode === "register" && (
            <>
              <div>
                <label className="input-label">Telegram (опц.)</label>
                <input className="input" value={form.telegram} onChange={(e) => setForm({...form, telegram: e.target.value})} placeholder="@username" />
              </div>
              <div>
                <label className="input-label">Реферальный код *</label>
                <input className="input" required minLength={4}
                  value={form.referralCode}
                  onChange={(e) => setForm({...form, referralCode: e.target.value.toUpperCase()})}
                  placeholder="TGC..." data-testid="reg-refcode" />
                <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 4 }}>
                  Регистрация только по приглашению. Возьмите ссылку у пригласителя.
                </div>
              </div>
              <label className="row gap-sm text-sm" style={{ marginTop: 8 }}>
                <input type="checkbox" required /> Мне 18+ и я согласен с Terms of Service
              </label>
            </>
          )}
          <button type="submit" className="btn btn-primary btn-full" disabled={loading} style={{ marginTop: 12 }} data-testid="auth-submit">
            {loading ? "..." : mode === "login" ? "Войти" : "Создать аккаунт"}
          </button>
          <button type="button" onClick={() => setMode(mode === "login" ? "register" : "login")}
            style={{ background: "transparent", border: "none", color: "var(--teal)", fontSize: 13, cursor: "pointer", marginTop: 8 }}>
            {mode === "login" ? "Нет аккаунта? Зарегистрироваться" : "Уже есть аккаунт? Войти"}
          </button>
        </form>
      </div>
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════════
   HOME SCREEN
═════════════════════════════════════════════════════════════════ */
export function HomeScreen({ onNav }) {
  const { user, wallets, tgcPrice, referral, fetchAll } = useStore();
  useAutoRefresh(() => fetchAll(), 15000);
  const totalTGC = wallets ? wallets.cashTgc + wallets.shoppingTgc + wallets.pensionTgc + wallets.insuranceTgc : 0;
  const totalEur = (totalTGC * tgcPrice).toFixed(2);

  return (
    <div className="screen">
      <div className="screen-header">
        <div className="row between">
          <div>
            <div style={{ fontSize: 12, opacity: 0.7 }}>Привет,</div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>{user?.firstName || "User"} 👋</div>
          </div>
          <EnergyBattery points={user?.energyPoints || 75} mini />
        </div>
      </div>

      {/* Total balance card */}
      <div className="wallet-card">
        <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 4 }}>Общий баланс</div>
        <div style={{ fontSize: 36, fontWeight: 900, letterSpacing: -1 }}>{totalTGC.toFixed(2)} TGC</div>
        <div style={{ fontSize: 14, opacity: 0.85 }}>≈ €{totalEur}</div>
        <div className="row between" style={{ marginTop: 16, position: "relative", zIndex: 1 }}>
          <span className="chip gold">1 TGC = €{tgcPrice}</span>
          {user?.kycStatus === "approved" ? (
            <span className="chip"><Shield size={11} style={{ marginRight: 4 }} /> KYC✓</span>
          ) : (
            <button onClick={() => onNav("profile")} style={{ background: "#E8A020", color: "#0D1B4B", border: "none", padding: "4px 10px", borderRadius: 999, fontSize: 11, fontWeight: 700 }}>
              Пройти KYC
            </button>
          )}
        </div>
      </div>

      {/* 4 wallets grid */}
      <div style={{ padding: "0 16px" }}>
        <div className="row between mb-sm">
          <span className="text-bold">4 Кошелька</span>
          <button onClick={() => onNav("wallet")} style={{ background: "transparent", border: "none", color: "var(--teal)", fontSize: 13 }}>
            Подробнее →
          </button>
        </div>
        <div className="grid-2 mb-md">
          <WalletMini label="Cash" amount={wallets?.cashTgc || 0} icon="💰" onClick={() => onNav("wallet-cash")} testId="home-wallet-cash" />
          <WalletMini label="Shopping" amount={wallets?.shoppingTgc || 0} icon="🛒" onClick={() => onNav("wallet-shopping")} testId="home-wallet-shopping" />
          <WalletMini label="Pension 8%" amount={wallets?.pensionTgc || 0} icon="🏛️" onClick={() => onNav("wallet-pension")} testId="home-wallet-pension" />
          <WalletMini label="Insurance" amount={wallets?.insuranceTgc || 0} icon="🛡️" onClick={() => onNav("wallet-insurance")} testId="home-wallet-insurance" />
        </div>

        <EnergyBattery points={user?.energyPoints || 75} />

        {/* Live Staking Ticker — investor WOW factor */}
        <LiveStakingTicker pensionTgc={wallets?.pensionTgc || 0} insuranceTgc={wallets?.insuranceTgc || 0} />

        {/* Quick actions */}
        <div className="mt-md">
          <span className="text-bold">Быстрые действия</span>
          <div className="grid-3 mt-sm">
            <QuickAction icon={<QrCode size={22} />} label="Scan QR" onClick={() => onNav("scanner")} />
            <QuickAction icon={<ArrowLeftRight size={22} />} label="Обменять" onClick={() => onNav("exchange-full")} />
            <QuickAction icon={<ShoppingBag size={22} />} label="Магазин" onClick={() => onNav("marketplace")} />
            <QuickAction icon={<TrendingUp size={22} />} label="Стейкинг" onClick={() => onNav("staking")} />
            <QuickAction icon={<Users size={22} />} label="Рефералы" onClick={() => onNav("referral")} />
            <QuickAction icon={<CreditCard size={22} />} label="Visa Карта" onClick={() => onNav("card")} />
            <QuickAction icon={<Bot size={22} />} label="AI Agent" onClick={() => onNav("ai")} />
            <QuickAction icon={<ArrowLeftRight size={22} />} label="P2P" onClick={() => onNav("p2p")} />
            <QuickAction icon={<Gift size={22} />} label="Magic 44" onClick={() => onNav("magic44")} />
          </div>
        </div>

        {/* Magic 44 progress */}
        {referral && (
          <div className="card mt-md" onClick={() => onNav("magic44")} style={{ cursor: "pointer", background: "linear-gradient(135deg, rgba(232,160,32,0.12) 0%, rgba(188,19,254,0.08) 100%)", borderColor: "rgba(232,160,32,0.3)" }} data-testid="home-magic44-card">
            <div className="row between mb-sm">
              <div className="row gap-sm">
                <Sparkles size={18} color="#E8A020" />
                <span className="text-bold">Magic 44 AirDrop</span>
              </div>
              <span className="chip gold">{referral.magicProgress} / 44</span>
            </div>
            <div className="energy-bar" style={{ height: 8 }}>
              <div className="energy-fill" style={{ width: `${(referral.magicProgress / 44) * 100}%`, background: "linear-gradient(90deg, #E8A020, #BC13FE)" }} />
            </div>
            <p className="text-sm text-muted mt-sm" style={{ margin: "8px 0 0" }}>
              Пригласи 44 активных пользователей → разблокируй Diamond stake + 5000 TGC бонус
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function WalletMini({ label, amount, icon, onClick, testId }) {
  return (
    <div className="card" style={{ padding: 12, cursor: onClick ? "pointer" : "default" }} onClick={onClick} data-testid={testId}>
      <div style={{ fontSize: 22 }}>{icon}</div>
      <div className="text-hint text-sm mt-sm">{label}</div>
      <div className="text-bold text-lg">{amount.toFixed(2)}</div>
      <div className="text-hint" style={{ fontSize: 10 }}>TGC</div>
    </div>
  );
}

function QuickAction({ icon, label, onClick }) {
  return (
    <button onClick={onClick}
      style={{
        display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
        background: "white", border: "1px solid var(--border)", borderRadius: 14,
        padding: "14px 4px", cursor: "pointer", color: "var(--navy)",
        fontSize: 11, fontWeight: 600,
      }}>
      {icon}
      <span>{label}</span>
    </button>
  );
}

/* ═════════════════════════════════════════════════════════════════
   MAP SCREEN — Merchants list + QR Pay
═════════════════════════════════════════════════════════════════ */
export function MapScreen() {
  const { merchants, payQR } = useStore();
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const filtered = merchants.filter((m) =>
    m.businessName.toLowerCase().includes(query.toLowerCase()) ||
    m.address?.toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div className="screen">
      <div className="screen-header">
        <div className="row between">
          <h1 className="screen-title">🗺️ Мерчанты</h1>
          <span className="chip" style={{ background: "rgba(255,255,255,0.15)", color: "white" }}>
            {merchants.length}+ точек
          </span>
        </div>
        <div className="row gap-sm" style={{ background: "rgba(255,255,255,0.15)", borderRadius: 12, padding: "10px 14px", marginTop: 12 }}>
          <Search size={16} color="white" opacity={0.7} />
          <input value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Поиск мерчантов..."
            style={{ flex: 1, background: "transparent", border: "none", color: "white", outline: "none", fontSize: 14 }}
            data-testid="merchant-search"
          />
        </div>
      </div>

      <div style={{ padding: 16 }}>
        {filtered.map((m) => (
          <div key={m.merchantId} className="card mb-sm" onClick={() => setSelected(m)}
            style={{ cursor: "pointer", padding: 14 }} data-testid={`merchant-${m.merchantId}`}>
            <div className="row between">
              <div className="flex-1">
                <div className="text-bold">{m.businessName}</div>
                <div className="row gap-sm text-sm text-hint mt-sm">
                  <MapPin size={12} />
                  <span>{m.address}</span>
                </div>
                <div className="row gap-sm mt-sm">
                  <span className="chip">{m.category}</span>
                  {m.energyBoost > 1 && <span className="chip gold"><Zap size={10} /> x{m.energyBoost}</span>}
                  <span className="text-sm text-hint"><Star size={11} /> {m.rating}</span>
                </div>
              </div>
              <ChevronRight size={18} color="#9CA3AF" />
            </div>
          </div>
        ))}
      </div>

      {selected && <PayModal merchant={selected} onClose={() => setSelected(null)} onPay={async (amt) => {
        await payQR(selected.merchantId, amt);
        setSelected(null);
      }} />}
    </div>
  );
}

function PayModal({ merchant, onClose, onPay }) {
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const tgcPrice = useStore((s) => s.tgcPrice);
  const tgcAmount = (parseFloat(amount) || 0) / tgcPrice;

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 500, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
      <div style={{ background: "white", borderRadius: "24px 24px 0 0", padding: 24, width: "100%", maxWidth: 430, animation: "slideUp 0.3s" }}>
        <div className="row between mb-md">
          <h3 style={{ margin: 0, color: "var(--navy)" }}>Оплатить {merchant.businessName}</h3>
          <button onClick={onClose} style={{ background: "transparent", border: "none", cursor: "pointer" }}><X size={24} /></button>
        </div>
        <div className="card" style={{ background: "#F5F6FA", marginBottom: 16 }}>
          <div className="text-hint text-sm">Сумма к оплате</div>
          <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)}
            placeholder="0.00"
            style={{ background: "transparent", border: "none", outline: "none", fontSize: 36, fontWeight: 800, width: "100%", color: "var(--navy)" }}
            data-testid="pay-amount" autoFocus
          />
          <div className="text-sm text-muted">EUR · будет списано {tgcAmount.toFixed(2)} TGC</div>
        </div>
        <div className="row gap-sm mb-md">
          {[10, 25, 50, 100].map((v) => (
            <button key={v} className="btn btn-outline" style={{ flex: 1, padding: "10px" }} onClick={() => setAmount(v.toString())}>
              €{v}
            </button>
          ))}
        </div>
        <button className="btn btn-teal btn-full" disabled={loading || !amount} onClick={async () => {
          setLoading(true);
          try { await onPay(parseFloat(amount)); } finally { setLoading(false); }
        }} data-testid="pay-confirm">
          {loading ? "..." : `Оплатить €${amount || 0}`}
        </button>
      </div>
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════════
   STAKING SCREEN
═════════════════════════════════════════════════════════════════ */
export function StakingScreen() {
  const { stakes, createStake, wallets, tgcPrice, fetchAll } = useStore();
  useAutoRefresh(() => fetchAll(), 15000);
  const [packages, setPackages] = useState([]);
  const [selected, setSelected] = useState(null);
  useEffect(() => { api.get("/staking/packages").then((r) => setPackages(r.data.packages)); }, []);

  return (
    <div className="screen">
      <div className="screen-header">
        <h1 className="screen-title">📈 Стейкинг</h1>
        <p className="screen-subtitle">5 пакетов · от 8% до 20% в месяц</p>
      </div>

      {stakes.length > 0 && (
        <div style={{ padding: "16px 16px 0" }}>
          <div className="text-bold mb-sm">Мои активные стейки</div>
          {stakes.map((s) => (
            <div key={s.stakeId} className="card mb-sm">
              <div className="row between mb-sm">
                <div className="text-bold">{s.packageName}</div>
                <span className="chip">{s.monthlyRate || (s.apy ? (s.apy/12).toFixed(1) : 0)}% / мес</span>
              </div>
              <div className="text-lg text-navy text-bold">{s.amountTgc.toFixed(0)} TGC</div>
              <div className="text-sm text-muted">Профит: +{s.profitTgc} TGC</div>
              <div className="energy-bar mt-sm" style={{ height: 6 }}>
                <div className="energy-fill" style={{ width: `${s.progressPct}%`, background: "var(--teal)" }} />
              </div>
              <div className="text-sm text-hint mt-sm">{s.daysElapsed} / {s.duration} дней</div>
            </div>
          ))}
        </div>
      )}

      <div style={{ padding: 16 }}>
        <div className="text-bold mb-sm">Выбери пакет</div>
        {packages.map((p) => {
          const tgcNeeded = p.amount / tgcPrice;
          const canAfford = wallets && wallets.cashTgc >= tgcNeeded;
          const months = Math.round(p.duration / 30);
          const totalProfit = (p.amount * p.monthlyRate / 100) * months;
          return (
            <div key={p.id} className="card mb-sm" onClick={() => setSelected(p)}
              style={{ cursor: "pointer", borderColor: selected?.id === p.id ? "var(--gold)" : "var(--border)", borderWidth: 2 }}
              data-testid={`stake-package-${p.id}`}>
              <div className="row between mb-sm">
                <div>
                  <div className="text-bold">{p.name} · €{p.amount}</div>
                  <div className="text-sm text-muted">{p.duration} дней ({months} {months === 1 ? "месяц" : months < 5 ? "месяца" : "месяцев"}) · {tgcNeeded.toFixed(0)} TGC</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div className="text-bold text-navy text-xl">{p.monthlyRate}%</div>
                  <div className="text-sm text-hint">в месяц</div>
                </div>
              </div>
              <div className="row between text-sm mt-sm">
                <span className="text-muted">Прибыль за период:</span>
                <span className="text-bold text-success">+€{totalProfit.toFixed(0)} ({(totalProfit/tgcPrice).toFixed(0)} TGC)</span>
              </div>
              {p.energyBonus > 0 && <div className="chip gold mt-sm"><Zap size={11} /> +{p.energyBonus}% Energy Bonus</div>}
              {!canAfford && <div className="text-sm text-error mt-sm">Недостаточно TGC в Cash (нужно {tgcNeeded.toFixed(0)})</div>}
            </div>
          );
        })}
        {selected && (
          <button className="btn btn-teal btn-full mt-md" onClick={async () => {
            await createStake(selected.id);
            setSelected(null);
          }} data-testid="stake-create-btn">
            Создать стейк {selected.name} · {(selected.amount / tgcPrice).toFixed(0)} TGC
          </button>
        )}
      </div>
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════════
   WALLET SCREEN
═════════════════════════════════════════════════════════════════ */
export function WalletScreen() {
  const { wallets, tgcPrice, transactions, transferWallet, fetchTransactions, fetchAll } = useStore();
  useAutoRefresh(() => { fetchAll(); fetchTransactions(); }, 15000);
  const [tab, setTab] = useState("all");
  const [transfer, setTransfer] = useState(null);
  useEffect(() => { fetchTransactions(); }, [fetchTransactions]);

  const walletList = [
    { key: "cash",      label: "Cash",      icon: "💰", desc: "Свободные TGC, можно стейкать и обменивать" },
    { key: "shopping",  label: "Shopping",  icon: "🛒", desc: "Оплата QR, Visa-карты, маркетплейс" },
    { key: "pension",   label: "Pension 8%",icon: "🏛️", desc: "Пенсионный фонд с капитализацией 8%/мес" },
    { key: "insurance", label: "Insurance", icon: "🛡️", desc: "Страховой резерв, разблокируется через 1 год" },
  ];

  return (
    <div className="screen">
      <div className="screen-header">
        <h1 className="screen-title">💼 Кошельки</h1>
        <div className="text-sm" style={{ opacity: 0.8 }}>
          Всего: {wallets ? (wallets.cashTgc + wallets.shoppingTgc + wallets.pensionTgc + wallets.insuranceTgc).toFixed(2) : 0} TGC
        </div>
      </div>
      <div style={{ padding: 16 }}>
        {walletList.map((w) => (
          <div key={w.key} className="card mb-sm">
            <div className="row between">
              <div>
                <div className="row gap-sm">
                  <span style={{ fontSize: 22 }}>{w.icon}</span>
                  <div>
                    <div className="text-bold">{w.label}</div>
                    <div className="text-sm text-hint">{w.desc}</div>
                  </div>
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div className="text-bold text-navy">{(wallets?.[`${w.key}Tgc`] || 0).toFixed(2)}</div>
                <div className="text-sm text-muted">TGC</div>
              </div>
            </div>
            <button className="btn btn-outline" style={{ width: "100%", marginTop: 10, padding: "8px" }}
              onClick={() => setTransfer({ from: w.key })} data-testid={`wallet-transfer-${w.key}`}>
              <ArrowLeftRight size={14} /> Перевести
            </button>
          </div>
        ))}

        <div className="text-bold mt-md mb-sm">История транзакций</div>
        {transactions.slice(0, 20).map((tx) => (
          <div key={tx.txId} className="tx-row">
            <div className="tx-icon" style={{ background: tx.amount > 0 ? "rgba(16,185,129,0.15)" : "rgba(239,68,68,0.1)" }}>
              {tx.amount > 0 ? <ArrowDownRight size={20} color="#10B981" /> : <ArrowUpRight size={20} color="#EF4444" />}
            </div>
            <div className="flex-1">
              <div className="text-bold text-sm">{tx.note || tx.type.replace(/_/g, " ")}</div>
              <div className="text-hint text-sm">{new Date(tx.createdAt).toLocaleString("ru-RU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div className={tx.amount > 0 ? "text-success text-bold" : "text-bold"}>
                {tx.amount > 0 ? "+" : ""}{(tx.amount || 0).toFixed(2)}
              </div>
              <div className="text-hint text-sm">TGC</div>
            </div>
          </div>
        ))}
      </div>
      {transfer && <TransferModal {...transfer} onClose={() => setTransfer(null)} />}
    </div>
  );
}

function TransferModal({ from, onClose }) {
  const [to, setTo] = useState("shopping");
  const [amount, setAmount] = useState("");
  const { transferWallet, wallets } = useStore();
  const available = wallets?.[`${from}Tgc`] || 0;
  const options = ["cash", "shopping", "pension", "insurance"].filter((k) => k !== from);

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 500, display: "flex", alignItems: "flex-end" }}>
      <div style={{ background: "white", borderRadius: "24px 24px 0 0", padding: 24, width: "100%", maxWidth: 430, margin: "0 auto" }}>
        <div className="row between mb-md">
          <h3 style={{ margin: 0, color: "var(--navy)" }}>Перевод между кошельками</h3>
          <button onClick={onClose} style={{ background: "transparent", border: "none" }}><X size={24} /></button>
        </div>
        <div className="input-label">Откуда</div>
        <div className="input" style={{ background: "#F5F6FA", marginBottom: 12 }}>
          {from} ({available.toFixed(2)} TGC доступно)
        </div>
        <div className="input-label">Куда</div>
        <select className="input mb-md" value={to} onChange={(e) => setTo(e.target.value)}>
          {options.map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
        <div className="input-label">Сумма TGC</div>
        <input type="number" className="input mb-md" value={amount} onChange={(e) => setAmount(e.target.value)}
          placeholder="0.00" max={available} data-testid="transfer-amount" />
        <button className="btn btn-primary btn-full" disabled={!amount || parseFloat(amount) > available}
          onClick={async () => {
            await transferWallet(from, to, parseFloat(amount));
            onClose();
          }} data-testid="transfer-confirm">
          Перевести {amount || 0} TGC
        </button>
      </div>
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════════
   REFERRAL SCREEN
═════════════════════════════════════════════════════════════════ */
export function ReferralScreen() {
  const { referral, user, fetchReferral } = useStore();
  const [copied, setCopied] = useState(false);
  useEffect(() => { fetchReferral(); }, [fetchReferral]);
  useAutoRefresh(() => fetchReferral(), 15000);
  if (!referral) return <div className="center-col"><div className="spinner" /></div>;

  const link = `${window.location.origin}?ref=${referral.referralCode}`;
  const levels = [
    { n: 1, pct: 20,  color: "#BC13FE" },
    { n: 2, pct: 10,  color: "#8B5CF6" },
    { n: 3, pct: 5,   color: "#3B82F6" },
    { n: 4, pct: 3,   color: "#06B6D4" },
    { n: 5, pct: 2,   color: "#10B981" },
    { n: 6, pct: 2,   color: "#E8A020" },
    { n: 7, pct: 1,   color: "#F97316" },
    { n: 8, pct: 1,   color: "#EF4444" },
  ];

  return (
    <div className="screen">
      <div className="screen-header">
        <h1 className="screen-title">👥 Рефералы</h1>
        <p className="screen-subtitle">8-уровневая сеть + Magic 44</p>
      </div>
      <div style={{ padding: 16 }}>
        <div className="card mb-md" style={{ background: "linear-gradient(135deg, #0D1B4B 0%, #0E6B5E 100%)", color: "white", textAlign: "center", padding: 24 }}>
          <div className="text-sm" style={{ opacity: 0.8 }}>Твой реферальный код</div>
          <div style={{ fontSize: 28, fontWeight: 900, letterSpacing: 2, margin: "8px 0 12px" }}>{referral.referralCode}</div>
          <div style={{ background: "white", padding: 12, borderRadius: 12, display: "inline-block" }}>
            <QRCodeSVG value={link} size={160} />
          </div>
          <div className="mt-md" style={{ display: "flex", gap: 8, justifyContent: "center" }}>
            <button className="btn btn-gold" onClick={() => {
              navigator.clipboard.writeText(link);
              setCopied(true); setTimeout(() => setCopied(false), 2000);
            }} data-testid="copy-refcode">
              {copied ? <Check size={16} /> : <Copy size={16} />}
              {copied ? "Скопировано" : "Копировать ссылку"}
            </button>
          </div>
        </div>

        <div className="card mb-md" style={{ background: "linear-gradient(135deg, rgba(232,160,32,0.1) 0%, rgba(188,19,254,0.1) 100%)" }}>
          <div className="row between mb-sm">
            <div className="text-bold"><Sparkles size={16} color="#E8A020" style={{ verticalAlign: "middle" }} /> Magic 44</div>
            <span className="chip gold">{referral.magicProgress} / 44</span>
          </div>
          <div className="energy-bar" style={{ height: 10 }}>
            <div className="energy-fill" style={{ width: `${(referral.magicProgress/44)*100}%`, background: "linear-gradient(90deg, #E8A020, #BC13FE)" }} />
          </div>
          <p className="text-sm text-muted mt-sm" style={{ margin: "8px 0 0" }}>
            Пригласи 44 активных → разблокируй Diamond stake + 5000 TGC
          </p>
        </div>

        <div className="text-bold mb-sm">Твои награды</div>
        <div className="card mb-md">
          <div className="row between">
            <div>
              <div className="text-hint text-sm">Заработано всего</div>
              <div className="text-2xl text-navy text-bold">{referral.totalRewardsTgc.toFixed(0)} TGC</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div className="text-hint text-sm">Сеть</div>
              <div className="text-2xl text-bold">{referral.totalNetwork}</div>
            </div>
          </div>
        </div>

        <div className="text-bold mb-sm">8 уровней комиссий <span className="chip gold" style={{ marginLeft: 8 }}>ИТОГО 44%</span></div>
        <p className="text-sm text-muted" style={{ marginTop: -4, marginBottom: 12 }}>
          Начисления мгновенно при любой транзакции (покупка, QR-оплата, доход участников твоей структуры)
        </p>
        {levels.map((l) => (
          <div key={l.n} className="card mb-sm" style={{ padding: 12 }}>
            <div className="row between">
              <div className="row gap-sm">
                <div style={{ width: 32, height: 32, borderRadius: 16, background: l.color, color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 13 }}>
                  L{l.n}
                </div>
                <div>
                  <div className="text-bold text-sm">Уровень {l.n}</div>
                  <div className="text-hint text-sm">{referral.levelCounts[l.n-1] || 0} человек</div>
                </div>
              </div>
              <div className="text-bold text-navy">{l.pct}%</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════════
   PROFILE SCREEN
═════════════════════════════════════════════════════════════════ */
export function ProfileScreen({ onNav }) {
  const { user, logout, submitKYC } = useStore();
  const kycChip = {
    not_started: { label: "Не пройден", color: "red" },
    pending:     { label: "Проверка...", color: "gold" },
    approved:    { label: "Подтверждён ✓", color: "" },
    rejected:    { label: "Отклонён", color: "red" },
  }[user?.kycStatus || "not_started"];

  return (
    <div className="screen">
      <div className="screen-header">
        <h1 className="screen-title">👤 Профиль</h1>
      </div>
      <div style={{ padding: 16 }}>
        <div className="card mb-md" style={{ textAlign: "center", padding: 24 }}>
          <div style={{ width: 72, height: 72, borderRadius: 36, background: "linear-gradient(135deg, #0D1B4B, #0E6B5E)", color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, fontWeight: 800, margin: "0 auto 12px" }}>
            {user?.firstName?.[0]}{user?.lastName?.[0]}
          </div>
          <div className="text-xl text-bold">{user?.firstName} {user?.lastName}</div>
          <div className="text-sm text-muted">{user?.email}</div>
          <div className="mt-sm">
            <span className={`chip ${kycChip.color}`}>KYC: {kycChip.label}</span>
          </div>
        </div>

        {user?.kycStatus !== "approved" && (
          <div className="card mb-md" style={{ background: "#FDF3DC" }}>
            <div className="row gap-sm mb-sm">
              <Shield size={18} color="#996700" />
              <span className="text-bold">Пройти KYC</span>
            </div>
            <p className="text-sm text-muted">
              Быстрая верификация (DEMO: просто нажми кнопку, одобрение через 3 сек).
              После KYC: выпуск Visa-карты, увеличенные лимиты, P2P без ограничений.
            </p>
            <button className="btn btn-gold btn-full mt-sm" onClick={submitKYC} disabled={user?.kycStatus === "pending"} data-testid="kyc-submit-btn">
              {user?.kycStatus === "pending" ? "Проверка..." : "Пройти KYC"}
            </button>
          </div>
        )}

        <div className="text-bold mb-sm mt-md">Настройки</div>
        <ProfileRow icon={<CreditCard size={18} />} label="Virtual Visa Card" onClick={() => onNav("card")} />
        <ProfileRow icon={<ShoppingBag size={18} />} label="Marketplace" onClick={() => onNav("marketplace")} />
        <ProfileRow icon={<ArrowLeftRight size={18} />} label="P2P Exchange" onClick={() => onNav("p2p")} />
        <ProfileRow icon={<Bot size={18} />} label="AI Assistant" onClick={() => onNav("ai")} />
        <ProfileRow icon={<Users size={18} />} label="DAO Голосования" onClick={() => useStore.getState().setToast("info", "DAO доступен после первого голосования Q2 2026")} />
        <ProfileRow icon={<PiggyBank size={18} />} label="Bear Charity Mission" onClick={() => useStore.getState().setToast("info", "1% всех комиссий → на благотворительность")} />
        
        <div className="text-bold mb-sm mt-md">Информация</div>
        <ProfileRow icon="📄" label="Whitepaper" onClick={() => window.open("/api/investor/file/documents/02_TOKENOMICS.pdf", "_blank")} />
        <ProfileRow icon="🛡️" label="Terms of Service" />
        <ProfileRow icon="🔒" label="Privacy Policy" />

        <button className="btn btn-outline btn-full mt-lg" onClick={logout} data-testid="logout-btn">
          <LogOut size={16} /> Выйти
        </button>
        <p className="text-center text-hint text-sm mt-md">TGC App v1.0 DEMO</p>
      </div>
    </div>
  );
}

function ProfileRow({ icon, label, onClick }) {
  return (
    <div className="card mb-sm" onClick={onClick} style={{ cursor: "pointer", padding: "12px 16px" }}>
      <div className="row gap-sm">
        {typeof icon === "string" ? <span style={{ fontSize: 18 }}>{icon}</span> : icon}
        <span className="flex-1">{label}</span>
        <ChevronRight size={18} color="#9CA3AF" />
      </div>
    </div>
  );
}
function LogOut(props) { return <svg {...props} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width={props.size||16} height={props.size||16}><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>; }
